import { EDUCATION_SITE_URL, educationPublicPath, isEducationHost } from '../utils/educationHost'
import { LOCALE_HTML_LANG, LOCALE_OG, LOCALE_URL_PREFIXES, stripLocalePrefix, type AppLocale } from '../utils/locale'

type Locale = AppLocale

type SeoPayload = {
  title: string
  description: string
  path: string
  image?: string
  keywords?: string
  robots?: string
  ogType?: string
  structuredData?: Record<string, unknown>
}

const SITE_NAME = 'Pacific Link International'
const LEGAL_NAME = 'Pacific Link Consulting Pty Ltd'
const DEFAULT_IMAGE = '/logo.png'
export const SITE_URL = (
  (import.meta.env.VITE_SITE_URL as string | undefined) || 'https://pacificlinkinternational.com'
).replace(/\/$/, '')

function getSiteUrl(): string {
  return SITE_URL
}

/** Strip query/hash, collapse /home alias, and normalize trailing slashes (English base path). */
export function normalizeCanonicalPath(path: string): string {
  let normalized = path.split('?')[0].split('#')[0]
  normalized = stripLocalePrefix(normalized)

  if (normalized === '/home') return '/'

  if (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1)
  }

  return normalized || '/'
}

function isEducationPublicPath(path: string): boolean {
  const normalized = path.split('?')[0].split('#')[0] || '/'
  if (educationPublicPath(normalized)) return true
  return isEducationHost()
}

/** Build an absolute canonical URL on the production non-www domain. */
export function buildCanonicalUrl(path: string): string {
  if (isEducationPublicPath(path)) {
    const pathname = path.split('?')[0].split('#')[0] || '/'
    let publicPath = educationPublicPath(pathname) || pathname
    if (publicPath.length > 1 && publicPath.endsWith('/')) publicPath = publicPath.slice(0, -1)
    if (!publicPath || publicPath === '/') return `${EDUCATION_SITE_URL}/`
    return `${EDUCATION_SITE_URL}${publicPath.startsWith('/') ? publicPath : `/${publicPath}`}`
  }

  let normalized = path.split('?')[0].split('#')[0]
  if (normalized === '/home') return `${SITE_URL}/`
  for (const code of LOCALE_URL_PREFIXES) {
    if (normalized === `/${code}/home`) return `${SITE_URL}/${code}`
  }
  if (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1)
  }
  if (!normalized || normalized === '/') return `${SITE_URL}/`
  return `${SITE_URL}${normalized.startsWith('/') ? normalized : `/${normalized}`}`
}

function getLocaleAlternateUrls(path: string): Record<string, string> {
  const basePath = normalizeCanonicalPath(path)
  const enPath = basePath || '/'
  const result: Record<string, string> = {
    en: buildCanonicalUrl(enPath),
  }

  for (const code of LOCALE_URL_PREFIXES) {
    const prefixed = basePath === '/' ? `/${code}` : `/${code}${basePath}`
    result[code] = buildCanonicalUrl(prefixed)
  }

  return result
}

function upsertMeta(selector: string, attrs: Record<string, string>, content: string) {
  let el = document.head.querySelector(selector) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    Object.entries(attrs).forEach(([k, v]) => el?.setAttribute(k, v))
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertLink(rel: string, href: string) {
  const existing = document.head.querySelectorAll(`link[rel="${rel}"]`)
  existing.forEach((node, index) => {
    if (index === 0) {
      node.setAttribute('href', href)
    } else {
      node.remove()
    }
  })

  if (existing.length === 0) {
    const el = document.createElement('link')
    el.setAttribute('rel', rel)
    el.setAttribute('href', href)
    document.head.appendChild(el)
  }
}

function upsertHreflangAlternates(path: string) {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((node) => node.remove())

  // Education section is Chinese-only — no multilingual alternates.
  const normalized = path.split('?')[0].split('#')[0]
  if (isEducationPublicPath(normalized)) {
    const href = buildCanonicalUrl(normalized)
    const el = document.createElement('link')
    el.setAttribute('rel', 'alternate')
    el.setAttribute('hreflang', 'zh-CN')
    el.setAttribute('href', href)
    document.head.appendChild(el)

    const xDefault = document.createElement('link')
    xDefault.setAttribute('rel', 'alternate')
    xDefault.setAttribute('hreflang', 'x-default')
    xDefault.setAttribute('href', href)
    document.head.appendChild(xDefault)
    return
  }

  const urls = getLocaleAlternateUrls(path)
  const alternates = [
    { hreflang: 'en', href: urls.en },
    { hreflang: 'zh-CN', href: urls.cn },
    { hreflang: 'ru', href: urls.ru },
    { hreflang: 'ar', href: urls.ar },
    { hreflang: 'ko', href: urls.ko },
    { hreflang: 'x-default', href: urls.en },
  ]

  for (const alternate of alternates) {
    const el = document.createElement('link')
    el.setAttribute('rel', 'alternate')
    el.setAttribute('hreflang', alternate.hreflang)
    el.setAttribute('href', alternate.href)
    document.head.appendChild(el)
  }
}

function upsertStructuredData(payload?: Record<string, unknown>) {
  const id = 'seo-structured-data'
  const existing = document.getElementById(id)

  if (!payload) {
    existing?.remove()
    return
  }

  const script = existing ?? document.createElement('script')
  script.id = id
  script.setAttribute('type', 'application/ld+json')
  script.textContent = JSON.stringify(payload)
  if (!existing) {
    document.head.appendChild(script)
  }
}

export function applySeo(payload: SeoPayload, locale: Locale) {
  const normalizedPath = payload.path.split('?')[0].split('#')[0]
  const isEducation = isEducationPublicPath(normalizedPath)
  const siteUrl = isEducation ? EDUCATION_SITE_URL : getSiteUrl()
  const canonical = buildCanonicalUrl(payload.path)
  const image = payload.image ? `${siteUrl}${payload.image}` : `${siteUrl}${DEFAULT_IMAGE}`
  const localeTag = LOCALE_HTML_LANG[locale]
  const ogLocale = LOCALE_OG[locale]
  const siteName = isEducation ? 'Pacific Link Education' : SITE_NAME

  document.title = payload.title
  document.documentElement.lang = localeTag

  upsertMeta('meta[name="description"]', { name: 'description' }, payload.description)
  upsertMeta('meta[name="robots"]', { name: 'robots' }, payload.robots ?? 'index, follow')
  upsertMeta('meta[name="keywords"]', { name: 'keywords' }, payload.keywords ?? '')
  upsertMeta('meta[property="og:type"]', { property: 'og:type' }, payload.ogType ?? 'website')
  upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, siteName)
  upsertMeta('meta[property="og:title"]', { property: 'og:title' }, payload.title)
  upsertMeta('meta[property="og:description"]', { property: 'og:description' }, payload.description)
  upsertMeta('meta[property="og:url"]', { property: 'og:url' }, canonical)
  upsertMeta('meta[property="og:image"]', { property: 'og:image' }, image)
  upsertMeta('meta[property="og:locale"]', { property: 'og:locale' }, ogLocale)
  upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image')
  upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, payload.title)
  upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, payload.description)
  upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, image)
  upsertLink('canonical', canonical)
  upsertHreflangAlternates(payload.path)
  upsertStructuredData(payload.structuredData)
}

type SeoL10n = { gb: string; cn: string; ru: string; ar: string; ko: string }
function pickSeo(locale: Locale, text: SeoL10n): string {
  return text[locale] ?? text.gb
}

const newsArticleSeo: Record<
  string,
  {
    title: SeoL10n
    description: SeoL10n
    keywords: SeoL10n
    image: string
    datePublished: string
  }
> = {
  'sierra-leone-go-for-growth-rebrand': {
    datePublished: '2026-11-02',
    image: '/sierra-leone-cover.jpg',
    title: {
      gb: 'Sierra Leone FDI Initiative Evolves from GO-FOR-GOLD to GO-FOR-GROWTH | Pacific Link International',
      cn: '塞拉利昂官方项目由 GO-FOR-GOLD 更名为 GO-FOR-GROWTH | Pacific Link International',
      ru: 'Инициатива Сьерра-Леоне по ПИИ: от GO-FOR-GOLD к GO-FOR-GROWTH | Pacific Link International',
      ar: 'مبادرة الاستثمار الأجنبي المباشر في سيراليون تنتقل من GO-FOR-GOLD إلى GO-FOR-GROWTH | Pacific Link International',
      ko: '시에라리온 FDI 이니셔티브, GO-FOR-GOLD에서 GO-FOR-GROWTH로 브랜드 전환 | Pacific Link International',
    },
    description: {
      gb: 'Effective 2 November 2026, Sierra Leone’s flagship FDI initiative formally renames from GO-FOR-GOLD to GO-FOR-GROWTH, aligned with Vision 2039. Existing contracts remain unchanged. Pacific Link continues as an Official Partner.',
      cn: '自 2026 年 11 月 2 日起，塞拉利昂旗舰外商投资倡议正式由 GO-FOR-GOLD 更名为 GO-FOR-GROWTH，与 Vision 2039 对齐。既有合同安排不变。Pacific Link 继续作为认证官方合作伙伴。',
      ru: 'С 2 ноября 2026 года флагманская инициатива ПИИ Сьерра-Леоне официально переименовывается из GO-FOR-GOLD в GO-FOR-GROWTH в рамках Vision 2039. Действующие договоры не меняются. Pacific Link остаётся официальным партнёром.',
      ar: 'اعتباراً من 2 نوفمبر 2026، يُعاد رسمياً تسمية مبادرة الاستثمار الأجنبي المباشر في سيراليون من GO-FOR-GOLD إلى GO-FOR-GROWTH بما يتوافق مع Vision 2039. الترتيبات التعاقدية القائمة لا تتغير. تواصل Pacific Link كونها شريكاً رسمياً.',
      ko: '2026년 11월 2일부터 시에라리온 국가 FDI 플래그십 이니셔티브가 GO-FOR-GOLD에서 GO-FOR-GROWTH로 공식 명칭 변경됩니다. Vision 2039와 정합하며, 기존 계약은 변동 없습니다. Pacific Link는 공식 파트너를 유지합니다.',
    },
    keywords: {
      gb: 'Sierra Leone GO-FOR-GROWTH, GO-FOR-GOLD rebrand, Sierra Leone citizenship, Vision 2039, FDI, Pacific Link',
      cn: '塞拉利昂GO-FOR-GROWTH,GO-FOR-GOLD更名,塞拉利昂入籍,Vision 2039,外商投资,Pacific Link',
      ru: 'GO-FOR-GROWTH Сьерра-Леоне, ребрендинг GO-FOR-GOLD, гражданство Сьерра-Леоне, Vision 2039, ПИИ, Pacific Link',
      ar: 'GO-FOR-GROWTH سيراليون, إعادة تسمية GO-FOR-GOLD, جنسية سيراليون, Vision 2039, استثمار أجنبي مباشر, Pacific Link',
      ko: '시에라리온 GO-FOR-GROWTH, GO-FOR-GOLD 리브랜드, 시에라리온 시민권, Vision 2039, FDI, Pacific Link',
    },
  },
  'malta-citizenship-by-merit-framework': {
    datePublished: '2026-10-23',
    image: '/malta-cover.jpg',
    title: {
      gb: 'Malta Citizenship by Merit: A Framework Built on Exceptional Contribution | Pacific Link International',
      cn: '马耳他功勋入籍：以卓越功绩为核心的裁量性框架 | Pacific Link International',
      ru: 'Гражданство Мальты по заслугам: дискреционная рамка на основе исключительного вклада | Pacific Link International',
      ar: 'جنسية مالطا على أساس الجدارة: إطار تقديري يقوم على مساهمة استثنائية | Pacific Link International',
      ko: '몰타 공적 시민권: 탁월한 공적을 중심으로 한 재량적 프레임워크 | Pacific Link International',
    },
    description: {
      gb: 'Malta Citizenship by Merit is a discretionary framework centred on exceptional contribution to Malta. Our Malta team draws on years of practice to assess each client’s own circumstances.',
      cn: '马耳他功勋入籍是一项以卓越功绩为核心的裁量性框架。我们的马耳他团队凭借多年实务经验，根据客户自身情况分析是否符合条件。',
      ru: 'Гражданство Мальты по заслугам — дискреционная рамка, в центре которой исключительный вклад в Мальту. Наша команда по Мальте с многолетней практикой оценивает обстоятельства каждого клиента.',
      ar: 'جنسية مالطا على أساس الجدارة إطار تقديري يقوم على مساهمة استثنائية لمالطا. يقيّم فريقنا لمالطا، بخبرة سنوات، ظروف كل عميل.',
      ko: '몰타 공적 시민권은 몰타에 대한 탁월한 기여를 중심으로 한 재량적 프레임워크입니다. 우리 몰타 팀은 다년간의 실무 경험으로 고객 본인의 조건을 분석합니다.',
    },
    keywords: {
      gb: 'Malta Citizenship by Merit, Malta citizenship framework, exceptional contribution, Pacific Link',
      cn: '马耳他功勋入籍,马耳他公民身份,卓越功绩,Pacific Link',
      ru: 'гражданство Мальты по заслугам, рамка гражданства Мальты, исключительный вклад, Pacific Link',
      ar: 'جنسية مالطا على أساس الجدارة, إطار الجنسية المالطية, مساهمة استثنائية, Pacific Link',
      ko: '몰타 공적 시민권, 몰타 시민권 프레임워크, 탁월한 공적, Pacific Link',
    },
  },
  'abc-radio-australia-nauru-citizenship-interview': {
    datePublished: '2026-10-21',
    image: '/img/news/abc_radio_australia_nauru_citizenship_interview.jpg',
    title: {
      gb: 'Pacific Link International Featured in ABC Radio Australia CBI Report | Pacific Link International',
      cn: 'Pacific Link International 接受 ABC 澳洲广播采访，谈瑙鲁投资入籍 | Pacific Link International',
      ru: 'Pacific Link International в материале ABC Radio Australia о гражданстве за инвестиции | Pacific Link International',
      ar: 'Pacific Link International في تقرير إذاعة ABC أستراليا عن جنسية الاستثمار | Pacific Link International',
      ko: 'ABC 라디오 호주 투자시민권 보도에 출연한 Pacific Link International | Pacific Link International',
    },
    description: {
      gb: 'Pacific Link discusses Nauru citizenship by investment, due diligence, and a statelessness case in an ABC Radio Australia interview.',
      cn: 'Pacific Link 在澳大利亚广播公司采访中讨论瑙鲁投资入籍、尽职调查，以及一宗无国籍人士取得国籍的案例。',
      ru: 'Pacific Link в интервью ABC Radio Australia — о гражданстве Науру за инвестиции, проверке благонадёжности и деле лица без гражданства.',
      ar: 'تناقش Pacific Link في مقابلة إذاعة ABC أستراليا جنسية ناورو عبر الاستثمار والعناية الواجبة وحالة شخص كان عديم الجنسية.',
      ko: 'Pacific Link이 ABC 라디오 호주 인터뷰에서 나우루 투자 시민권, 실사, 무국적자의 시민권 취득 사례를 이야기합니다.',
    },
    keywords: {
      gb: 'Nauru citizenship, citizenship by investment, ABC Radio Australia, due diligence, Pacific Link',
      cn: '瑙鲁公民身份,投资入籍,澳大利亚广播公司,尽职调查,Pacific Link',
      ru: 'гражданство Науру, гражданство за инвестиции, ABC Radio Australia, проверка благонадёжности, Pacific Link',
      ar: 'جنسية ناورو, الجنسية عبر الاستثمار, إذاعة ABC أستراليا, العناية الواجبة, Pacific Link',
      ko: '나우루 시민권, 투자 시민권, ABC 라디오 호주, 실사, Pacific Link',
    },
  },
  'saint-vincent-redesigned-citizenship-programme-q1-2027': {
    datePublished: '2026-10-16',
    image: '/img/news/saint_vincent_cbi_q1_2027.jpg',
    title: {
      gb: 'Saint Vincent Signals a Redesigned Citizenship Programme for Early 2027 | Pacific Link International',
      cn: '圣文森特拟于2027年初推出不同于传统模式的投资入籍 | Pacific Link International',
      ru: 'Сент-Винсент обозначил переработанную программу гражданства с возможным стартом в начале 2027 года | Pacific Link International',
      ar: 'سانت فينسنت وجزر غرينادين تشير إلى برنامج جنسية مُعاد تصميمه مع إمكانية البدء مطلع 2027 | Pacific Link International',
      ko: '세인트빈센트, 기존과 다른 시민권 프로그램을 2027년 초에 추진 | Pacific Link International',
    },
    description: {
      gb: 'Saint Vincent and the Grenadines is still planning an investment-linked citizenship route, but not a conventional CBI scheme. Early 2027 is a possible window. No fees or application rules have been published.',
      cn: '圣文森特和格林纳丁斯仍计划开设与投资挂钩的公民身份通道，但不会沿用传统投资入籍模式。2027年初是一个可能的窗口。费用与申请规则尚未公布。',
      ru: 'Сент-Винсент и Гренадины по-прежнему планируют маршрут гражданства, связанный с инвестициями, но не классический CBI. Начало 2027 года — возможное окно. Сборы и правила подачи не опубликованы.',
      ar: 'ما زالت سانت فينسنت وجزر غرينادين تخطط لمسار جنسية مرتبط بالاستثمار، لكن ليس بنموذج الجنسية عبر الاستثمار التقليدي. مطلع 2027 نافذة محتملة. الرسوم وقواعد التقديم لم تُنشر.',
      ko: '세인트빈센트 그레나딘은 투자와 연결된 시민권 경로를 계속 추진하지만, 기존 투자 시민권 방식은 아닙니다. 2027년 초가 가능한 시점입니다. 수수료와 신청 규정은 아직 공개되지 않았습니다.',
    },
    keywords: {
      gb: 'Saint Vincent citizenship, Saint Vincent and the Grenadines CBI, Caribbean citizenship by investment, OECS, Pacific Link',
      cn: '圣文森特入籍,圣文森特和格林纳丁斯,投资入籍,加勒比公民身份,东加勒比,Pacific Link',
      ru: 'гражданство Сент-Винсента, Сент-Винсент и Гренадины, гражданство за инвестиции, Карибы, OECS, Pacific Link',
      ar: 'جنسية سانت فينسنت, سانت فينسنت وجزر غرينادين, الجنسية عبر الاستثمار, الكاريبي, Pacific Link',
      ko: '세인트빈센트 시민권, 세인트빈센트 그레나딘, 투자 시민권, 카리브해, OECS, Pacific Link',
    },
  },
  'sierra-leone-citizenship-simple-documents-remote-application': {
    datePublished: '2026-10-16',
    image: '/img/news/sierra_leone_citizenship_remote_application_2026.jpg',
    title: {
      gb: 'Sierra Leone Citizenship: Straightforward Documents, a Short Timeline and a Remote Application | Pacific Link International',
      cn: '塞拉利昂入籍项目：材料简单、办理快捷，可远程完成申请 | Pacific Link International',
      ru: 'Гражданство Сьерра-Леоне: короткий пакет документов, сжатые сроки и дистанционная подача | Pacific Link International',
      ar: 'جنسية سيراليون: مستندات مبسطة، ومدة أقصر، وطلب يمكن إتمامه عن بُعد | Pacific Link International',
      ko: '시에라리온 시민권: 간소한 서류, 짧은 처리 기간, 원격 신청 | Pacific Link International',
    },
    description: {
      gb: 'Sierra Leone citizenship and passport through GO-FOR-GROWTH: a lighter document list, an indicative 2–3 month window for the passport and naturalisation certificate, and a fully remote application. Pacific Link is an official partner.',
      cn: '塞拉利昂公民身份（护照）可通过 GO-FOR-GROWTH 远程申请。基础材料相对精简，护照及归化证书签发参考周期约为2至3个月。Pacific Link 为官方合作伙伴。',
      ru: 'Гражданство и паспорт Сьерра-Леоне по программе GO-FOR-GROWTH: короткий пакет документов, ориентир 2–3 месяца на паспорт и сертификат о натурализации, подача полностью дистанционно. Pacific Link — официальный партнёр.',
      ar: 'جنسية وجواز سفر سيراليون عبر GO-FOR-GROWTH: مستندات أساسية مختصرة، ومدة مرجعية من شهرين إلى ثلاثة أشهر لجواز السفر وشهادة التجنيس، وطلب عن بُعد بالكامل. Pacific Link شريك رسمي.',
      ko: 'GO-FOR-GROWTH를 통한 시에라리온 시민권 및 여권. 기본 서류는 비교적 간소하고, 여권·귀화증서 발급 참고 기간은 약 2–3개월이며, 신청은 원격으로 진행됩니다. Pacific Link는 공식 파트너입니다.',
    },
    keywords: {
      gb: 'Sierra Leone passport, Sierra Leone citizenship, GO-FOR-GROWTH, remote citizenship application, second passport, Pacific Link',
      cn: '塞拉利昂护照,塞拉利昂公民身份,塞拉利昂入籍,GO-FOR-GROWTH,远程申请,第二护照,Pacific Link',
      ru: 'паспорт Сьерра-Леоне, гражданство Сьерра-Леоне, GO-FOR-GROWTH, дистанционная подача, второй паспорт, Pacific Link',
      ar: 'جواز سفر سيراليون, جنسية سيراليون, GO-FOR-GROWTH, طلب عن بُعد, جواز سفر ثانٍ, Pacific Link',
      ko: '시에라리온 여권, 시에라리온 시민권, GO-FOR-GROWTH, 원격 신청, 두 번째 여권, Pacific Link',
    },
  },
}

export function buildSeoByRoute(routeName: string, path: string, locale: Locale, countryParam?: string): SeoPayload {
  const pages: Record<string, SeoPayload> = {
    home: {
      title: pickSeo(locale, {
        gb: 'Global Residency & Citizenship by Investment Firm | Pacific Link International',
        cn: '全球居留与投资入籍机构 | Pacific Link International',
        ru: 'Global Residency & Citizenship by Investment Firm | Pacific Link International',
        ar: 'Global Residency & Citizenship by Investment Firm | Pacific Link International',
        ko: 'Global Residency & Citizenship by Investment Firm | Pacific Link International',
      }),
      description: pickSeo(locale, {
        gb: 'Pacific Link is an investment migration firm, an officially authorised agent for Naoero (Nauru) and São Tomé and Príncipe, and an accredited Official Partner for Sierra Leone GO-FOR-GROWTH.',
        cn: 'Pacific Link 是投资移民机构，亦是瑙鲁与圣多美和普林西比公民身份项目的官方授权直接中介，以及塞拉利昂 GO-FOR-GROWTH 认证官方合作伙伴。',
        ru: 'Pacific Link — компания в сфере инвестиционной миграции, официальный уполномоченный агент программ гражданства Науру и Сан-Томе и Принсипи и аккредитованный официальный партнёр Сьерра-Леоне GO-FOR-GROWTH.',
        ar: 'Pacific Link شركة للهجرة الاستثمارية ووكيل مباشر معتمد رسمياً لبرنامجي جنسية ناورو وساو تومي وبرينسيب وشريك رسمي معتمد لبرنامج سيراليون GO-FOR-GROWTH.',
        ko: 'Pacific Link는 투자이민 전문 기관이자 나우루 및 상투메 프린시페 시민권 프로그램의 공식 인가 에이전트이며, 시에라리온 GO-FOR-GROWTH의 공인 공식 파트너입니다.',
      }),
      path,
      keywords: pickSeo(locale, {
        gb: 'authorised Naoero agent, authorised Nauru agent, São Tomé licensed agent, Sierra Leone official partner, GO-FOR-GROWTH, investment migration consultants, citizenship by investment, Pacific Link',
        cn: '瑙鲁官方授权中介,圣多美持牌代理,塞拉利昂官方合作伙伴,GO-FOR-GROWTH,投资移民顾问,投资入籍,Pacific Link',
        ru: 'уполномоченный агент Науру, лицензированный агент Сан-Томе, официальный партнёр Сьерра-Леоне, GO-FOR-GROWTH, консультанты по инвестиционной миграции, гражданство за инвестиции, Pacific Link',
        ar: 'وكيل معتمد لناورو, وكيل مرخص لساو تومي, شريك رسمي لسيراليون, GO-FOR-GROWTH, مستشارو الهجرة الاستثمارية, الجنسية عن طريق الاستثمار, Pacific Link',
        ko: '나우루 공식 인가 에이전트, 상투메 공인 에이전트, 시에라리온 공식 파트너, GO-FOR-GROWTH, 투자이민 컨설턴트, 투자 시민권, Pacific Link',
      }),
    },
    programs: {
      title: pickSeo(locale, {
        gb: 'Investment Migration Programs | Pacific Link International',
        cn: '投资移民项目总览 | Pacific Link International',
        ru: 'Программы инвестиционной миграции | Pacific Link International',
        ar: 'برامج الهجرة الاستثمارية | Pacific Link International',
        ko: '투자이민 프로그램 | Pacific Link International',
      }),
      description: pickSeo(locale, {
        gb: 'Explore Pacific Link residency and citizenship programmes, including Naoero (Nauru), Vanuatu, São Tomé, Sierra Leone, Argentina and Malta.',
        cn: '查看 Pacific Link 的居留与公民身份项目，包括瑙鲁、瓦努阿图、圣多美、塞拉利昂、阿根廷和马耳他。',
        ru: 'Ознакомьтесь с программами резидентства и гражданства Pacific Link, включая Науру, Вануату, Сан-Томе, Сьерра-Леоне, Аргентину и Мальту.',
        ar: 'تعرّف على برامج الإقامة والجنسية لدى Pacific Link، بما في ذلك ناورو وفانواتو وساو تومي وسيراليون والأرجنتين ومالطا.',
        ko: '나우루, 바누아투, 상투메, 시에라리온, 아르헨티나, 몰타를 포함한 Pacific Link의 거주 및 시민권 프로그램을 확인하세요.',
      }),
      path,
    },
    services: {
      title: pickSeo(locale, {
        gb: 'Our Services | Pacific Link International',
        cn: '服务内容 | Pacific Link International',
        ru: 'Наши услуги | Pacific Link International',
        ar: 'خدماتنا | Pacific Link International',
        ko: '서비스 | Pacific Link International',
      }),
      description: pickSeo(locale, {
        gb: 'End-to-end support for application preparation, due diligence, compliance, and post-approval services.',
        cn: '提供申请材料准备、尽职调查支持、合规咨询及获批后持续服务。',
        ru: 'Комплексное сопровождение подготовки заявления, проверки благонадёжности, соблюдения требований и обслуживания после одобрения.',
        ar: 'دعم متكامل لإعداد الطلبات والعناية الواجبة والامتثال والخدمات اللاحقة للموافقة.',
        ko: '신청서 준비, 실사, 컴플라이언스 및 승인 후 서비스를 아우르는 종합 지원을 제공합니다.',
      }),
      path,
    },
    news: {
      title: pickSeo(locale, {
        gb: 'Insights & Updates | Pacific Link International',
        cn: '新闻动态 | Pacific Link International',
        ru: 'Аналитика и обновления | Pacific Link International',
        ar: 'رؤى وتحديثات | Pacific Link International',
        ko: '인사이트 & 업데이트 | Pacific Link International',
      }),
      description: pickSeo(locale, {
        gb: 'Company news, programme updates, case highlights and practical insights on investment migration from Pacific Link.',
        cn: 'Pacific Link 公司新闻、项目动态、案例亮点与投资移民实务洞察。',
        ru: 'Новости компании, обновления программ, кейсы и практическая аналитика по инвестиционной миграции от Pacific Link.',
        ar: 'أخبار الشركة وتحديثات البرامج وأبرز الحالات ورؤى عملية حول الهجرة الاستثمارية من Pacific Link.',
        ko: 'Pacific Link의 회사 소식, 프로그램 업데이트, 사례 하이라이트 및 투자이민 실무 인사이트.',
      }),
      path,
    },
    'news-detail': {
      title: pickSeo(locale, {
        gb: 'News Article | Pacific Link International',
        cn: '新闻详情 | Pacific Link International',
        ru: 'Новостная статья | Pacific Link International',
        ar: 'مقال إخباري | Pacific Link International',
        ko: '뉴스 기사 | Pacific Link International',
      }),
      description: pickSeo(locale, {
        gb: 'Read Pacific Link news and updates on Pacific investment migration programs.',
        cn: '阅读 Pacific Link 新闻详情，了解太平洋投资移民与项目动态。',
        ru: 'Читайте новости Pacific Link и обновления по программам инвестиционной миграции в Тихоокеанском регионе.',
        ar: 'اقرأ أخبار Pacific Link وتحديثات برامج الهجرة الاستثمارية في منطقة المحيط الهادئ.',
        ko: '태평양 지역 투자이민 프로그램에 관한 Pacific Link 뉴스와 최신 소식을 읽어보세요.',
      }),
      path,
      keywords: pickSeo(locale, {
        gb: 'Pacific Link, news, investment migration',
        cn: 'Pacific Link,新闻,投资移民',
        ru: 'Pacific Link, новости, инвестиционная миграция',
        ar: 'Pacific Link, أخبار, هجرة استثمارية',
        ko: 'Pacific Link, 뉴스, 투자이민',
      }),
    },
    'about-us': {
      title: pickSeo(locale, {
        gb: 'About Pacific Link International | Authorisations and Leadership',
        cn: '关于 Pacific Link International | 授权资质与领导团队',
        ru: 'О Pacific Link International | Полномочия и руководство',
        ar: 'عن Pacific Link International | التفويضات والقيادة',
        ko: 'Pacific Link International 소개 | 인가 및 리더십',
      }),
      description: pickSeo(locale, {
        gb: 'Pacific Link International is an international investment migration advisory network. Strategic leadership is based in Australia, with international business development, client engagement and programme delivery through coordinated operating entities, government authorisations and professional partners.',
        cn: 'Pacific Link International 是国际投资移民咨询网络。战略领导层设于澳大利亚；国际业务拓展、客户对接与项目交付通过运营实体、政府授权与专业合作伙伴的协同结构开展。',
        ru: 'Pacific Link International — международная консультационная сеть по инвестиционной миграции. Стратегическое руководство базируется в Австралии; международное развитие бизнеса, работа с клиентами и реализация программ осуществляются через операционные организации, государственные разрешения и профессиональных партнёров.',
        ar: 'Pacific Link International هي شبكة استشارات دولية للهجرة الاستثمارية. تقع القيادة الاستراتيجية في أستراليا، بينما يُنفَّذ تطوير الأعمال الدولي وارتباط العملاء وتسليم البرامج عبر كيانات تشغيلية وتفويضات حكومية وشركاء مهنيين منسقين.',
        ko: 'Pacific Link International은 국제 투자 이민 자문 네트워크입니다. 전략 리더십은 호주에 기반하며, 국제 사업 개발, 고객 대응 및 프로그램 제공은 운영 법인, 정부 인가 및 전문 파트너의 조율된 구조를 통해 이루어집니다.',
      }),
      path,
    },
    partnership: {
      title: pickSeo(locale, {
        gb: 'Partner with Us | Licensed Naoero, São Tomé, Sierra Leone & Vanuatu | Pacific Link International',
        cn: '代理合作 | 瑙鲁、圣多美、塞拉利昂与瓦努阿图持牌通道 | Pacific Link International',
        ru: 'Партнёрство с нами | Лицензированный агент Науру, Сан-Томе, Сьерра-Леоне и Вануату | Pacific Link International',
        ar: 'شراكة معنا | وكيل مرخّص لـ ناورو وساو تومي وسيراليون وفانواتو | Pacific Link International',
        ko: '파트너십 | 공인 나우루·상투메·시에라리온·바누아투 채널 | Pacific Link International',
      }),
      description: pickSeo(locale, {
        gb: 'Partner with Pacific Link. We are a licensed Naoero (Nauru) ECRCP agent, a São Tomé CIP Marketing Agent and an accredited Official Partner for Sierra Leone GO-FOR-GROWTH; Vanuatu PR and DSP are delivered through our licensed Vanuatu entity, with compliant case support and a transparent partnership model.',
        cn: 'Pacific Link 诚邀中介代理合作。我们是瑙鲁 ECRCP 持牌中介、圣多美 CIP 持牌市场代理，以及塞拉利昂 GO-FOR-GROWTH 认证官方合作伙伴；瓦努阿图永居与 DSP 通过我们的瓦努阿图持牌实体交付，提供合规通道、案件支持与透明合作机制。',
        ru: 'Станьте партнёром Pacific Link. Мы лицензированный агент Науру ECRCP, маркетинговый агент São Tomé CIP и аккредитованный официальный партнёр Сьерра-Леоне GO-FOR-GROWTH; ВНЖ и DSP Вануату реализуются через нашу лицензированную организацию в Вануату, с сопровождением кейсов и прозрачной моделью сотрудничества.',
        ar: 'انضم إلى Pacific Link كشريك. نحن وكيل مرخص لبرنامج ناورو ECRCP ووكيل تسويقي مرخّص لـ São Tomé CIP وشريك رسمي معتمد لبرنامج سيراليون GO-FOR-GROWTH؛ وتُقدَّم إقامة فانواتو الدائمة وDSP عبر كياننا المرخّص في فانواتو، مع دعم للملفات ونموذج شراكة شفاف.',
        ko: 'Pacific Link과 파트너가 되십시오. 공인 나우루 ECRCP 에이전트, 상투메 CIP 마케팅 에이전트, 시에라리온 GO-FOR-GROWTH 공인 공식 파트너이며, 바누아투 영주권과 DSP는 공인 바누아투 법인을 통해 제공됩니다. 규정에 부합하는 프로그램 접근, 케이스 지원 및 투명한 파트너십 모델을 제공합니다.',
      }),
      path,
      keywords: pickSeo(locale, {
        gb: 'agent partnership, immigration agent network, Naoero passport, Nauru passport, São Tomé citizenship, Sierra Leone citizenship, GO-FOR-GROWTH, Vanuatu PR, Vanuatu DSP, licensed agent, investment migration',
        cn: '代理合作,中介合作,瑙鲁护照,圣多美入籍,塞拉利昂入籍,GO-FOR-GROWTH,瓦努阿图永居,瓦努阿图DSP,持牌中介,投资移民代理',
        ru: 'агентское партнёрство, сеть иммиграционных агентов, паспорт Науру, гражданство Сан-Томе, гражданство Сьерра-Леоне, GO-FOR-GROWTH, ВНЖ Вануату, DSP Вануату, лицензированный агент, инвестиционная миграция',
        ar: 'شراكة وكلاء, شبكة وكلاء هجرة, جواز سفر ناورو, جنسية ساو تومي, جنسية سيراليون, GO-FOR-GROWTH, إقامة فانواتو, DSP فانواتو, وكيل مرخص, هجرة استثمارية',
        ko: '에이전트 파트너십, 이민 에이전트 네트워크, 나우루 여권, 상투메 시민권, 시에라리온 시민권, GO-FOR-GROWTH, 바누아투 영주권, 바누아투 DSP, 공인 에이전트, 투자이민',
      }),
    },
    'contact-us': {
      title: pickSeo(locale, {
        gb: 'Contact Us | Pacific Link International',
        cn: '联系我们 | Pacific Link International',
        ru: 'Связаться с нами | Pacific Link International',
        ar: 'تواصل معنا | Pacific Link International',
        ko: '문의하기 | Pacific Link International',
      }),
      description: pickSeo(locale, {
        gb: 'Contact Pacific Link for Vanuatu PR, Vanuatu citizenship, and Naoero (Nauru) citizenship consultations.',
        cn: '联系 Pacific Link 团队，获取瓦努阿图PR、瓦努阿图护照和瑙鲁护照项目咨询。',
        ru: 'Свяжитесь с Pacific Link для консультации по ВНЖ Вануату, гражданству Вануату и Науру.',
        ar: 'تواصل مع Pacific Link للحصول على استشارة حول الإقامة الدائمة في فانواتو وجنسية فانواتو وناورو.',
        ko: '바누아투 영주권, 바누아투 시민권 및 나우루 시민권 상담을 위해 Pacific Link에 문의하세요.',
      }),
      path,
      robots: 'noindex',
    },
    'education-home': {
      title: 'Pacific Link International Education | 国际大学在线硕博',
      description:
        'Pacific Link Education 对接美国、英国、法国等国际正规大学在线硕博项目，覆盖 MBA、金融、人工智能、心理学、教育学、法学与博士等方向，免联考申请、在职可学。',
      path,
      image: '/education-home-cover.png',
      keywords:
        'Pacific Link Education,国际大学在线硕博,免联考,在职硕博,美国,英国,法国,MBA,DBA,学历教育',
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Pacific Link Education',
        url: buildCanonicalUrl(path),
        inLanguage: 'zh-CN',
        description:
          '国际正规大学在线硕博项目咨询与申请服务，覆盖美国、英国、法国等合作院校。',
        publisher: {
          '@type': 'Organization',
          name: 'Pacific Horizon Advisory Limited',
          url: SITE_URL,
        },
      },
    },
    'education-programs': {
      title: '国际大学在线硕博项目列表 | Pacific Link International Education',
      description:
        '浏览美国、英国、法国等国际正规大学在线硕士、博士及硕博连读项目，查看学制、学费、课程与学位认证说明，预约顾问一对一咨询。',
      path,
      image: '/education-home-cover.png',
      keywords: '国际在线硕博,在线硕士,在线博士,硕博连读,MBA,DBA,教育学,心理学,数据科学,学费,Pacific Link Education',
    },
    'education-program-detail': {
      title: '项目详情 | Pacific Link International Education',
      description: '查看国际大学在线硕博项目的课程、学习模式、学费、学生权益与学位认证说明。',
      path,
      image: '/education-home-cover.png',
      keywords: '项目详情,国际在线硕博,在线硕士,在线博士,硕博连读,学费,申请费,Pacific Link Education',
    },
    'education-university': {
      title: '合作大学及认证信息 | Pacific Link International Education',
      description:
        '了解合作国际大学的权威认证、排名亮点与学位国际认证路径，覆盖美国、英国、法国等院校。',
      path,
      image: '/education-home-cover.png',
      keywords: '国际大学认证,WASC,HLC,AACSB,AMBA,EFMD,QAA,WES,海牙认证,合作院校',
    },
    'education-how-it-works': {
      title: '申请流程 | Pacific Link International Education',
      description:
        '了解从咨询选项目、签订协议、递交材料与申请费，到录取缴费、学籍注册与入学安排的完整申请路径。',
      path,
      image: '/education-home-cover.png',
      keywords: '在线硕博申请流程,免联考申请,学籍注册,Pacific Link Education',
    },
    'education-faq': {
      title: '常见问题 | Pacific Link International Education',
      description:
        '解答教学模式、学位证书效力、国际认证、留服边界、学生权益、入学条件与学费支付等常见问题。',
      path,
      image: '/education-home-cover.png',
      keywords: '在线硕博常见问题,学位认证,WES,海牙认证,留服,在职学习',
    },
    'education-contact': {
      title: '咨询预约 | Pacific Link International Education',
      description: '通过邮箱、WhatsApp 或微信联系顾问，了解国际大学在线硕博项目与申请资格评估。',
      path,
      image: '/education-home-cover.png',
      keywords: '教育咨询,预约顾问,WhatsApp,微信,Pacific Link Education',
      robots: 'noindex',
    },
    'legal-notice': {
      title: pickSeo(locale, {
        gb: 'Legal Notice | Pacific Link International',
        cn: '法律声明 | Pacific Link International',
        ru: 'Юридическое уведомление | Pacific Link International',
        ar: 'إشعار قانوني | Pacific Link International',
        ko: '법적 고지 | Pacific Link International',
      }),
      description: pickSeo(locale, {
        gb: 'Legal notice for the Pacific Link website, including website operator details, international network structure, and important disclaimers.',
        cn: 'Pacific Link 网站法律声明，包括网站运营主体、国际网络结构及重要免责说明。',
        ru: 'Юридическое уведомление сайта Pacific Link: сведения об операторе, структуре международной сети и важные оговорки.',
        ar: 'الإشعار القانوني لموقع Pacific Link، بما في ذلك تفاصيل مشغّل الموقع وهيكل الشبكة الدولية وإخلاءات المسؤولية المهمة.',
        ko: 'Pacific Link 웹사이트 법적 고지 — 웹사이트 운영 주체, 국제 네트워크 구조 및 중요 면책 고지.',
      }),
      path,
    },
    'privacy-policy': {
      title: pickSeo(locale, {
        gb: 'Privacy Policy | Pacific Link International',
        cn: '隐私政策 | Pacific Link International',
        ru: 'Политика конфиденциальности | Pacific Link International',
        ar: 'سياسة الخصوصية | Pacific Link International',
        ko: '개인정보 처리방침 | Pacific Link International',
      }),
      description: pickSeo(locale, {
        gb: 'How Pacific Link collects, uses, discloses and protects personal information in connection with investment migration advisory services.',
        cn: '了解 Pacific Link 如何在投资移民咨询服务中收集、使用、披露与保护个人信息。',
        ru: 'Как Pacific Link собирает, использует, раскрывает и защищает персональные данные в связи с консультационными услугами по инвестиционной миграции.',
        ar: 'كيف تجمع Pacific Link المعلومات الشخصية وتستخدمها وتفصح عنها وتحميها في سياق خدمات استشارات الهجرة الاستثمارية.',
        ko: 'Pacific Link가 투자 이민 자문 서비스와 관련하여 개인정보를 수집·이용·공개·보호하는 방법을 안내합니다.',
      }),
      path,
    },
    'terms-of-use': {
      title: pickSeo(locale, {
        gb: 'Terms of Use | Pacific Link International',
        cn: '使用条款 | Pacific Link International',
        ru: 'Условия использования | Pacific Link International',
        ar: 'شروط الاستخدام | Pacific Link International',
        ko: '이용약관 | Pacific Link International',
      }),
      description: pickSeo(locale, {
        gb: 'Terms governing access to and use of the Pacific Link website, including information disclaimers and responsible use requirements.',
        cn: '规范访问与使用 Pacific Link 网站的条款，包括信息免责声明与合理使用要求。',
        ru: 'Условия доступа к сайту Pacific Link и его использования, включая оговорки об информации и требования ответственного использования.',
        ar: 'الشروط التي تحكم الوصول إلى موقع Pacific Link واستخدامه، بما في ذلك إخلاءات المسؤولية ومتطلبات الاستخدام المسؤول.',
        ko: 'Pacific Link 웹사이트 이용에 관한 약관 — 정보 면책 고지 및 책임 있는 이용 요건을 포함합니다.',
      }),
      path,
    },
  }

  if (routeName === 'program') {
    const programMap: Record<string, SeoPayload> = {
      residency_vanuatu: {
        title: pickSeo(locale, {
          gb: 'Vanuatu Permanent Residency Program | Pacific Link International',
          cn: '瓦努阿图永久居留 | Pacific Link International',
          ru: 'Программа постоянного резидентства Вануату | Pacific Link International',
          ar: 'برنامج الإقامة الدائمة في فانواتو | Pacific Link International',
          ko: '바누아투 영주권 프로그램 | Pacific Link International',
        }),
        description: pickSeo(locale, {
          gb: 'Vanuatu permanent residency from USD 7,000 — 10-year validity, approval in around 10 business days, no physical presence required, fully remote.',
          cn: '瓦努阿图永久居留：USD 7,000 起，10 年有效期，约 10 个工作日获批，无需实际入境，100% 远程办理。',
          ru: 'Постоянное резидентство Вануату от USD 7,000: срок действия 10 лет, одобрение примерно за 10 рабочих дней, физическое присутствие не требуется, полностью дистанционно.',
          ar: 'الإقامة الدائمة في فانواتو من USD 7,000 — صلاحية 10 سنوات، وموافقة خلال نحو 10 أيام عمل، دون حضور فعلي وبإجراءات عن بُعد بالكامل.',
          ko: '바누아투 영주권: USD 7,000부터, 10년 유효, 약 10영업일 내 승인, 현지 체류 없이 전 과정 원격 진행.',
        }),
        path,
        keywords: pickSeo(locale, {
          gb: 'Vanuatu PR, Vanuatu permanent residency',
          cn: '瓦努阿图永久居留,瓦努阿图PR,瓦努阿图居留',
          ru: 'ВНЖ Вануату, постоянное резидентство Вануату, тихоокеанское резидентство',
          ar: 'الإقامة الدائمة في فانواتو, إقامة فانواتو, الإقامة في المحيط الهادئ',
          ko: '바누아투 영주권, 바누아투 영구 거주, 태평양 거주권',
        }),
      },
      residency_sierra_leone: {
        title: pickSeo(locale, {
          gb: 'Sierra Leone Permanent Residency | Pacific Link International',
          cn: '塞拉利昂永久居留 | Pacific Link International',
          ru: 'Постоянное резидентство Сьерра-Леоне | Pacific Link International',
          ar: 'الإقامة الدائمة في سيراليون | Pacific Link International',
          ko: '시에라리온 영주권 | Pacific Link International',
        }),
        description: pickSeo(locale, {
          gb: 'Sierra Leone GO-FOR-GROWTH permanent residency from USD 70,000 for a single applicant. Dependant classes match citizenship, and due diligence is listed separately on the fee table.',
          cn: '塞拉利昂 GO-FOR-GROWTH 永久居留：单人 USD 70,000。附属申请人分类与入籍相同，尽职调查费在价格表中单独列出。',
          ru: 'Постоянное резидентство Сьерра-Леоне GO-FOR-GROWTH от USD 70 000 за одного заявителя. Категории иждивенцев те же, что у гражданства, проверка благонадёжности указана отдельно.',
          ar: 'الإقامة الدائمة في سيراليون عبر GO-FOR-GROWTH من 70,000 دولار للمتقدم الواحد. فئات المعالين هي نفسها المعتمدة للجنسية، والعناية الواجبة معروضة بشكل منفصل.',
          ko: '시에라리온 GO-FOR-GROWTH 영주권은 단독 신청자 USD 70,000부터입니다. 동반가족 구분은 시민권과 같으며, 실사는 비용표에 별도로 표시됩니다.',
        }),
        path,
        keywords: pickSeo(locale, {
          gb: 'Sierra Leone permanent residency, Sierra Leone PR, GO-FOR-GROWTH, Sierra Leone residence',
          cn: '塞拉利昂永居,塞拉利昂永久居留,GO-FOR-GROWTH,塞拉利昂居留',
          ru: 'постоянное резидентство Сьерра-Леоне, ВНЖ Сьерра-Леоне, GO-FOR-GROWTH',
          ar: 'الإقامة الدائمة في سيراليون, إقامة سيراليون, GO-FOR-GROWTH',
          ko: '시에라리온 영주권, 시에라리온 영구 거주, GO-FOR-GROWTH',
        }),
      },
      residency_malta: {
        title: pickSeo(locale, {
          gb: 'Malta Permanent Residence Program (MPRP) | Pacific Link International',
          cn: '马耳他永久居留（MPRP）| Pacific Link International',
          ru: 'Постоянное резидентство Мальты (MPRP) | Pacific Link International',
          ar: 'برنامج الإقامة الدائمة في مالطا (MPRP) | Pacific Link International',
          ko: '몰타 영주권 (MPRP) | Pacific Link International',
        }),
        description: pickSeo(locale, {
          gb: 'Malta Permanent Residence Program (MPRP) from about €170,000 — indefinite residence, Schengen visa-free travel, no minimum stay. Processing typically 4–6 months.',
          cn: '马耳他永久居留计划（MPRP）约 €170,000 起：永久居留、申根免签、无最低居住天数，办理周期约 4–6 个月。',
          ru: 'Программа постоянного резидентства Мальты (MPRP) от примерно €170,000 — бессрочное проживание, безвизовый Шенген, без минимального пребывания. Срок обычно 4–6 месяцев.',
          ar: 'برنامج الإقامة الدائمة في مالطا (MPRP) من حوالي €170,000 — إقامة غير محددة المدة، سفر شنغن بدون تأشيرة، بلا حد أدنى للإقامة. المعالجة عادة 4–6 أشهر.',
          ko: '몰타 영주권 프로그램(MPRP) 약 €170,000부터 — 무기한 거주, 셰겐 무비자, 최소 체류 없음. 처리 기간 보통 4–6개월.',
        }),
        path,
        keywords: pickSeo(locale, {
          gb: 'Malta permanent residence, MPRP, Malta residency by investment, Schengen',
          cn: '马耳他永久居留,马耳他永居,MPRP,马耳他居留,申根',
          ru: 'постоянное резидентство Мальты, MPRP, ВНЖ Мальты, Шенген',
          ar: 'الإقامة الدائمة في مالطا, MPRP, إقامة مالطا, شنغن',
          ko: '몰타 영주권, MPRP, 몰타 거주, 셰겐',
        }),
      },
      residency_malaysia_mm2h: {
        title: pickSeo(locale, {
          gb: 'Malaysia MM2H Program | Pacific Link International',
          cn: '马来西亚第二家园（MM2H）| Pacific Link International',
          ru: 'Программа Malaysia MM2H | Pacific Link International',
          ar: 'برنامج Malaysia MM2H | Pacific Link International',
          ko: 'Malaysia MM2H 프로그램 | Pacific Link International',
        }),
        description: pickSeo(locale, {
          gb: 'Malaysia My Second Home (MM2H) long-term residency program with structured tiers and family inclusion options.',
          cn: '马来西亚第二家园（MM2H）长期居留项目：支持家庭申请、组别清晰，适合教育、医疗与生活配置。',
          ru: 'Программа долгосрочного проживания Malaysia My Second Home (MM2H) со структурированными категориями и возможностью включения членов семьи.',
          ar: 'برنامج Malaysia My Second Home (MM2H) للإقامة طويلة الأجل، بفئات منظمة وخيارات لإدراج أفراد الأسرة.',
          ko: 'Malaysia My Second Home (MM2H) 장기 거주 프로그램으로, 체계적인 등급과 가족 동반 옵션을 제공합니다.',
        }),
        path,
        keywords: pickSeo(locale, {
          gb: 'Malaysia MM2H, Malaysia long-term residency, My Second Home',
          cn: '马来西亚第二家园,MM2H,马来西亚长期居留',
          ru: 'Malaysia MM2H, долгосрочное резидентство в Малайзии, My Second Home',
          ar: 'Malaysia MM2H, إقامة طويلة الأجل في ماليزيا, My Second Home',
          ko: 'Malaysia MM2H, 말레이시아 장기 거주, My Second Home',
        }),
      },
      residency_thailand: {
        title: pickSeo(locale, {
          gb: 'Thailand Residency — Privilege Card & LTR | Pacific Link International',
          cn: '泰国居留（Privilege Card / LTR）| Pacific Link International',
          ru: 'Резидентство Таиланда — Privilege Card и LTR | Pacific Link International',
          ar: 'إقامة تايلاند — Privilege Card و LTR | Pacific Link International',
          ko: '태국 거주 — Privilege Card & LTR | Pacific Link International',
        }),
        description: pickSeo(locale, {
          gb: 'Thailand residency options in one place: Privilege Card membership visa and LTR long-term resident visa. Switch programmes on the detail page.',
          cn: '泰国居留一站式入口：含 Privilege Card 精英会员签证与 LTR 长期居留签证，详情页可切换项目。',
          ru: 'Варианты резидентства Таиланда: членская виза Privilege Card и виза LTR. Переключайте программы на странице деталей.',
          ar: 'خيارات إقامة تايلاند في مكان واحد: تأشيرة عضوية Privilege Card وتأشيرة LTR. بدّل البرامج في صفحة التفاصيل.',
          ko: '태국 거주 옵션을 한곳에서: Privilege Card 멤버십 비자와 LTR 장기 거주 비자. 상세 페이지에서 프로그램을 전환하세요.',
        }),
        path,
        keywords: pickSeo(locale, {
          gb: 'Thailand residency, Thailand Privilege Card, Thailand LTR visa, Thailand Elite',
          cn: '泰国居留,泰国精英签证,泰国LTR,Thailand Privilege Card',
          ru: 'резидентство Таиланда, Thailand Privilege Card, виза Thailand LTR',
          ar: 'إقامة تايلاند, Thailand Privilege Card, تأشيرة Thailand LTR',
          ko: '태국 거주, Thailand Privilege Card, Thailand LTR 비자',
        }),
      },
      citizenship_vanuatu: {
        title: pickSeo(locale, {
          gb: 'Vanuatu Citizenship by Investment | Pacific Link International',
          cn: '瓦努阿图公民身份（护照）| Pacific Link International',
          ru: 'Гражданство Вануату за инвестиции | Pacific Link International',
          ar: 'جنسية فانواتو عن طريق الاستثمار | Pacific Link International',
          ko: '바누아투 투자 시민권 | Pacific Link International',
        }),
        description: pickSeo(locale, {
          gb: 'Vanuatu DSP citizenship by investment from USD 130,000, second passport in around 2 months, visa-free access to 90+ countries.',
          cn: '瓦努阿图公民身份（护照），DSP 发展支持计划：单人 USD 130,000 起，约 2 个月获批，90+ 国家免签。',
          ru: 'Гражданство Вануату за инвестиции по программе DSP от USD 130,000: второй паспорт примерно за 2 месяца и безвизовый доступ в более чем 90 стран.',
          ar: 'جنسية فانواتو عن طريق الاستثمار ضمن برنامج DSP من USD 130,000، مع جواز سفر ثانٍ خلال نحو شهرين ودخول بدون تأشيرة إلى أكثر من 90 دولة.',
          ko: 'USD 130,000부터 시작하는 바누아투 DSP 투자 시민권 프로그램으로, 약 2개월 내 두 번째 여권 취득 및 90개국 이상 무비자 입국이 가능합니다.',
        }),
        path,
        keywords: pickSeo(locale, {
          gb: 'Vanuatu passport, Vanuatu citizenship, DSP program',
          cn: '瓦努阿图护照,瓦努阿图公民身份,瓦努阿图入籍,DSP发展支持计划',
          ru: 'паспорт Вануату, гражданство Вануату, программа DSP',
          ar: 'جواز سفر فانواتو, جنسية فانواتو, برنامج DSP',
          ko: '바누아투 여권, 바누아투 시민권, DSP 프로그램',
        }),
      },
      citizenship_nauru: {
        title: pickSeo(locale, {
          gb: 'Naoero (Nauru) Citizenship by Investment | Official Authorised Agent | Pacific Link International',
          cn: '瑙鲁公民身份（护照）| Pacific Link International',
          ru: 'Гражданство Науру за инвестиции | Официальный уполномоченный агент | Pacific Link International',
          ar: 'جنسية ناورو عن طريق الاستثمار | وكيل معتمد رسمي مباشر | Pacific Link International',
          ko: '나우루 투자 시민권 | 공식 인가 직접 에이전트 | Pacific Link International',
        }),
        description: pickSeo(locale, {
          gb: 'Apply for Naoero (Nauru) citizenship by investment through Pacific Link — an officially authorised direct agent with streamlined processing and licensed case support.',
          cn: '瑙鲁公民身份（护照）。Pacific Link 为官方授权直接中介，流程简洁、支持远程办理，案件全程合规跟进。',
          ru: 'Оформляйте гражданство Науру за инвестиции через Pacific Link — официального уполномоченного прямого агента с оптимизированным процессом и лицензированным сопровождением.',
          ar: 'قدّم لجنسية ناورو عن طريق الاستثمار عبر Pacific Link — وكيل مباشر معتمد رسمياً بإجراءات مبسطة ودعم مرخّص للملفات.',
          ko: '공식 인가 직접 에이전트 Pacific Link를 통해 나우루 투자 시민권을 신청하세요. 간소화된 절차와 공인 케이스 지원을 제공합니다.',
        }),
        path,
        keywords: pickSeo(locale, {
          gb: 'Naoero passport, Nauru passport, Naoero citizenship by investment, Nauru citizenship by investment, official authorised agent, licensed Naoero agent, licensed Nauru agent',
          cn: '瑙鲁护照,瑙鲁公民身份,瑙鲁入籍,官方授权直接中介,持牌中介',
          ru: 'паспорт Науру, гражданство Науру за инвестиции, официальный уполномоченный агент',
          ar: 'جواز سفر ناورو, جنسية ناورو عن طريق الاستثمار, وكيل معتمد رسمي',
          ko: '나우루 여권, 나우루 투자 시민권, 공식 인가 에이전트',
        }),
      },
      citizenship_sao_tome: {
        title: pickSeo(locale, {
          gb: 'São Tomé Citizenship by Investment | Official Authorised Agent | Pacific Link International',
          cn: '圣多美和普林西比公民身份（护照）| Pacific Link International',
          ru: 'Гражданство Сан-Томе за инвестиции | Официальный уполномоченный агент | Pacific Link International',
          ar: 'جنسية ساو تومي عن طريق الاستثمار | وكيل معتمد رسمي مباشر | Pacific Link International',
          ko: '상투메 투자 시민권 | 공식 인가 직접 에이전트 | Pacific Link International',
        }),
        description: pickSeo(locale, {
          gb: 'São Tomé and Príncipe citizenship by investment via the National Transformation Fund — apply through Pacific Link, an officially authorised direct Marketing Agent.',
          cn: '圣多美和普林西比公民身份（护照）：向国家转型基金捐款。Pacific Link 为官方授权直接市场代理，全程远程、合规递交。',
          ru: 'Гражданство Сан-Томе и Принсипи за инвестиции через Национальный фонд трансформации — подача через Pacific Link, официального уполномоченного прямого маркетингового агента.',
          ar: 'جنسية ساو تومي وبرينسيب عن طريق الاستثمار عبر صندوق التحول الوطني — قدّم عبر Pacific Link، وكيلاً تسويقياً مباشراً معتمداً رسمياً.',
          ko: '국가 전환 기금 기부를 통한 상투메 프린시페 투자 시민권 — 공식 인가 직접 마케팅 에이전트 Pacific Link를 통해 신청하세요.',
        }),
        path,
        keywords: pickSeo(locale, {
          gb: 'São Tomé passport, São Tomé and Príncipe citizenship by investment, official authorised agent, licensed Marketing Agent',
          cn: '圣多美护照,圣多美公民身份,圣多美和普林西比入籍,官方授权直接中介,持牌市场代理',
          ru: 'паспорт Сан-Томе, гражданство Сан-Томе и Принсипи за инвестиции, официальный уполномоченный агент',
          ar: 'جواز سفر ساو تومي, جنسية ساو تومي وبرينسيب عن طريق الاستثمار, وكيل معتمد رسمي',
          ko: '상투메 여권, 상투메 프린시페 투자 시민권, 공식 인가 에이전트',
        }),
      },
      citizenship_argentina: {
        title: pickSeo(locale, {
          gb: 'Argentina Citizenship by Investment | Pacific Link International',
          cn: '阿根廷公民身份（护照）| Pacific Link International',
          ru: 'Гражданство Аргентины за инвестиции | Pacific Link International',
          ar: 'جنسية الأرجنتين عن طريق الاستثمار | Pacific Link International',
          ko: '아르헨티나 투자 시민권 | Pacific Link International',
        }),
        description: pickSeo(locale, {
          gb: 'Argentina citizenship by investment expected to open in Q4 2026 — contribution from USD 350,000, plus official APCI, due diligence and administration fees. Service fee on enquiry.',
          cn: '阿根廷公民身份（护照）预计 2026 年第四季度开放：国库捐款起价 USD 350,000，另含 APCI、尽职调查及行政管理等官方费用；服务费面议。',
          ru: 'Гражданство Аргентины за инвестиции ожидается к открытию в IV квартале 2026: взнос от USD 350,000 плюс официальные сборы APCI, due diligence и администрирования. Сервисный сбор по запросу.',
          ar: 'يُتوقع فتح برنامج الجنسية عبر الاستثمار في الأرجنتين في الربع الرابع من 2026 — مساهمة من 350,000 دولار بالإضافة إلى رسوم APCI والعناية الواجبة والإدارة. رسوم الخدمة عند الطلب.',
          ko: '아르헨티나 투자 시민권은 2026년 4분기 접수 예정 — 국고 기부 USD 350,000부터, APCI·실사·행정 등 공식 수수료 별도. 서비스 비용은 문의.',
        }),
        path,
        keywords: pickSeo(locale, {
          gb: 'Argentina passport, Argentina citizenship by investment, MERCOSUR, second passport',
          cn: '阿根廷护照,阿根廷公民身份,阿根廷入籍,南方共同市场',
          ru: 'паспорт Аргентины, гражданство Аргентины за инвестиции, MERCOSUR, второй паспорт',
          ar: 'جواز سفر الأرجنتين, جنسية الأرجنتين عن طريق الاستثمار, ميركوسور, جواز سفر ثانٍ',
          ko: '아르헨티나 여권, 아르헨티나 투자 시민권, MERCOSUR, 두 번째 여권',
        }),
      },
      citizenship_sierra_leone: {
        title: pickSeo(locale, {
          gb: 'Sierra Leone Citizenship by Investment | Pacific Link International',
          cn: '塞拉利昂投资入籍（护照）| Pacific Link International',
          ru: 'Гражданство Сьерра-Леоне за инвестиции | Pacific Link International',
          ar: 'جنسية سيراليون عن طريق الاستثمار | Pacific Link International',
          ko: '시에라리온 투자 시민권 | Pacific Link International',
        }),
        description: pickSeo(locale, {
          gb: 'Sierra Leone citizenship by investment via GO-FOR-GROWTH, with fast-track, legacy, two-applicant enterprise, and individual entrepreneur pathways. Solo from USD 95,000 and two-applicant enterprise at USD 80,000 each. Due diligence is listed separately on the fee table.',
          cn: '塞拉利昂投资入籍（护照）：含快速、传承、双人企业与个人企业家四条路径。单人 USD 95,000 起，两人合办每人 USD 80,000，尽职调查费在价格表中单独列出。',
          ru: 'Гражданство Сьерра-Леоне за инвестиции по программе GO-FOR-GROWTH: четыре пути. Для одного заявителя от USD 95 000, для двоих по USD 80 000. Проверка благонадёжности указана отдельно в таблице сборов.',
          ar: 'جنسية سيراليون عن طريق الاستثمار عبر GO-FOR-GROWTH بأربعة مسارات. المتقدم الواحد من 95,000 دولار، والمتقدمان بـ 80,000 دولار لكل منهما. العناية الواجبة معروضة بشكل منفصل في جدول الرسوم.',
          ko: 'GO-FOR-GROWTH를 통한 시에라리온 투자 시민권의 네 가지 경로. 1인 USD 95,000부터, 2인은 1인당 USD 80,000. 실사는 비용표에 별도로 표시됩니다.',
        }),
        path,
        keywords: pickSeo(locale, {
          gb: 'Sierra Leone citizenship by investment, Sierra Leone CBI, Sierra Leone passport, Sierra Leone citizenship, GO-FOR-GROWTH',
          cn: '塞拉利昂投资入籍,塞拉利昂护照,塞拉利昂公民身份,塞拉利昂入籍,GO-FOR-GROWTH',
          ru: 'гражданство Сьерра-Леоне за инвестиции, паспорт Сьерра-Леоне, гражданство Сьерра-Леоне, GO-FOR-GROWTH',
          ar: 'جنسية سيراليون عن طريق الاستثمار, جواز سفر سيراليون, جنسية سيراليون, GO-FOR-GROWTH',
          ko: '시에라리온 투자 시민권, 시에라리온 여권, 시에라리온 시민권, GO-FOR-GROWTH',
        }),
      },
      citizenship_malta: {
        title: pickSeo(locale, {
          gb: 'Malta Citizenship by Merit | Pacific Link International',
          cn: '马耳他功勋入籍 | Pacific Link International',
          ru: 'Гражданство Мальты по заслугам | Pacific Link International',
          ar: 'جنسية مالطا على أساس الجدارة | Pacific Link International',
          ko: '몰타 공적 시민권 | Pacific Link International',
        }),
        description: pickSeo(locale, {
          gb: 'Malta Citizenship by Merit is a discretionary framework centred on exceptional service or contribution of strategic value to Malta. Whether and how to proceed depends on the applicant’s own circumstances.',
          cn: '马耳他功勋入籍是一项裁量性框架，核心是对马耳他承担具有战略价值的卓越功绩。能否办理以及如何推进，须根据个人条件单独分析。',
          ru: 'Гражданство Мальты по заслугам — дискреционная рамка, основанная на исключительных заслугах стратегической ценности для Мальты. Возможность и порядок рассмотрения зависят от обстоятельств заявителя.',
          ar: 'جنسية مالطا على أساس الجدارة إطار تقديري يقوم على مساهمة استثنائية ذات قيمة استراتيجية لمالطا. إمكانية المتابعة وكيفيتها تعتمدان على ظروف المتقدم.',
          ko: '몰타 공적 시민권은 몰타에 전략적 가치가 있는 탁월한 공적을 중심으로 한 재량적 프레임워크입니다. 진행 가능 여부와 방식은 신청인 개인의 조건에 따라 판단합니다.',
        }),
        path,
        keywords: pickSeo(locale, {
          gb: 'Malta Citizenship by Merit, EU citizenship, MCBM',
          cn: '马耳他功勋入籍,马耳他公民身份,欧盟公民身份',
          ru: 'гражданство Мальты по заслугам, гражданство ЕС, MCBM',
          ar: 'جنسية مالطا على أساس الجدارة, جنسية الاتحاد الأوروبي',
          ko: '몰타 공적 시민권, EU 시민권, MCBM',
        }),
      },
    }

    const selected = countryParam ? programMap[countryParam] : undefined
    if (selected) {
      return {
        ...selected,
        structuredData: {
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: selected.title,
          provider: {
            '@type': 'Organization',
            name: SITE_NAME,
            legalName: LEGAL_NAME,
            url: SITE_URL,
          },
          serviceType: pickSeo(locale, {
            gb: 'Investment migration consulting',
            cn: '投资移民咨询服务',
            ru: 'Консультации по инвестиционной миграции',
            ar: 'استشارات الهجرة الاستثمارية',
            ko: '투자이민 컨설팅',
          }),
          areaServed: 'Worldwide',
          url: buildCanonicalUrl(path),
          description: selected.description,
        },
      }
    }
  }

  if (routeName === 'news-detail') {
    const slug = path.split('?')[0].split('#')[0].split('/').filter(Boolean).pop()
    const article = slug ? newsArticleSeo[slug] : undefined
    if (article) {
      const title = pickSeo(locale, article.title)
      const description = pickSeo(locale, article.description)
      return {
        title,
        description,
        path,
        image: article.image,
        keywords: pickSeo(locale, article.keywords),
        ogType: 'article',
        structuredData: {
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          headline: title.replace(/ \| Pacific Link International$/, ''),
          datePublished: article.datePublished,
          dateModified: article.datePublished,
          image: `${SITE_URL}${article.image}`,
          description,
          inLanguage: LOCALE_HTML_LANG[locale],
          author: {
            '@type': 'Organization',
            name: 'Pacific Link',
            url: SITE_URL,
          },
          publisher: {
            '@type': 'Organization',
            name: SITE_NAME,
            legalName: LEGAL_NAME,
            url: SITE_URL,
          },
          mainEntityOfPage: buildCanonicalUrl(path),
        },
      }
    }
  }

  return (
    pages[routeName] ?? {
      title: pickSeo(locale, {
        gb: 'Pacific Link International',
        cn: 'Pacific Link International',
        ru: 'Pacific Link International',
        ar: 'Pacific Link International',
        ko: 'Pacific Link International',
      }),
      description: pickSeo(locale, {
        gb: 'Pacific Link official website',
        cn: 'Pacific Link 官方网站',
        ru: 'Официальный сайт Pacific Link',
        ar: 'الموقع الرسمي لـ Pacific Link',
        ko: 'Pacific Link 공식 웹사이트',
      }),
      path,
    }
  )
}

function truncateSeoText(text: string, max = 155): string {
  const normalized = text.replace(/\s+/g, ' ').trim()
  if (normalized.length <= max) return normalized
  return `${normalized.slice(0, max - 1).trim()}…`
}

function parseEducationTuitionOffer(tuition?: string): { priceCurrency: string; price: string } | null {
  const raw = tuition?.trim()
  if (!raw) return null

  // Prefer the first priced segment, e.g. "$7,040 / ¥49,800" or "¥99,800".
  const segment = raw.split('/')[0]?.trim() || raw
  const digits = segment.replace(/[^0-9.]/g, '')
  if (!digits) return null

  if (/¥|￥|人民币|CNY/i.test(segment) || (/¥|￥|人民币|CNY/i.test(raw) && !/\$|USD/i.test(segment))) {
    return { priceCurrency: 'CNY', price: digits }
  }
  if (/\$|USD/i.test(segment)) {
    return { priceCurrency: 'USD', price: digits }
  }
  // Fallback: RMB-only pages often omit currency letters after formatting as ¥.
  if (raw.includes('¥') || raw.includes('￥')) {
    return { priceCurrency: 'CNY', price: digits }
  }
  return { priceCurrency: 'USD', price: digits }
}

/** Build program-detail SEO after content JSON is loaded. */
export function buildEducationProgramSeo(input: {
  path: string
  university: string
  universityAbbr: string
  nameZh: string
  nameEn: string
  degree: string
  summary: string
  tuition?: string
  cover?: string
  country?: string
  language?: string
}): SeoPayload {
  const title = `${input.universityAbbr} ${input.nameZh} ${input.degree} | Pacific Link International Education`
  const tuitionPart = input.tuition?.trim() ? `学费 ${input.tuition.trim()}。` : ''
  const countryPart = input.country?.trim() ? `${input.country.trim()}合作院校` : '国际合作院校'
  const languagePart = input.language?.trim() ? `，${input.language.trim()}` : ''
  const description = truncateSeoText(
    `${input.university}${input.nameZh}（${input.degree}）${countryPart}在线硕博项目${languagePart}。${tuitionPart}${input.summary}`,
  )

  const structuredData: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: `${input.nameZh} ${input.degree}`,
    alternateName: input.nameEn,
    description,
    provider: {
      '@type': 'CollegeOrUniversity',
      name: input.university,
    },
    inLanguage: 'zh-CN',
    url: buildCanonicalUrl(input.path),
  }

  const offer = parseEducationTuitionOffer(input.tuition)
  if (offer) {
    structuredData.offers = {
      '@type': 'Offer',
      category: 'Tuition',
      priceCurrency: offer.priceCurrency,
      price: offer.price,
    }
  }

  return {
    title,
    description,
    path: input.path,
    image: input.cover || '/education-home-cover.png',
    keywords: [
      input.university,
      input.universityAbbr,
      input.nameZh,
      input.degree,
      input.nameEn,
      input.country,
      '在线硕博',
      '免联考',
      'Pacific Link Education',
    ]
      .filter(Boolean)
      .join(','),
    structuredData,
  }
}
